import { useMemo, useState } from "react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";

import style from "../ReferralForm.module.css";
import { ChooseFileAttachment } from "./ChooseFileAttachment/ChooseFileAttachment";
import { InvestigationSection } from "./InvestigationSection";

const EMPTY_ARRAY = [];

export const InvestigationsAndAttachmentsFormSection = ({
  form,
  isLungReferral,
  isProstateReferral,
  laboratoryResults,
  radiologyResults,
  documents,
  selectedFacility,
}) => {
  const [isFileAttachmentsModalOpen, setIsFileAttachmentsModalOpen] =
    useState(false);

  const attachmentRules = selectedFacility?.attachments;
  const maxAttachments = attachmentRules?.maxAttachments;

  const maxFileSizeMb = attachmentRules?.maxFileSizeMB;
  const maxFileSizeImageMb = attachmentRules?.maxImageSizeMB;

  const hasAttachmentLimit =
    Number.isFinite(maxAttachments) && maxAttachments > 0;
  const allowedExtensions = useMemo(() => {
    if (!attachmentRules) return [];
    const raw =
      [attachmentRules.allowedImageTypes, attachmentRules.allowedFileTypes]
        .filter(Boolean)
        .join(",") || "";
    return raw
      .split(",")
      .map((ext) => ext.trim().toLowerCase())
      .filter(Boolean);
  }, [attachmentRules]);

  const watchedFileAttachments = form.watch("fileAttachment");
  const selectedFileIds = Array.isArray(watchedFileAttachments)
    ? watchedFileAttachments
    : EMPTY_ARRAY;

  const selectedFiles = useMemo(() => {
    if (!Array.isArray(selectedFileIds) || !documents?.length) {
      return [];
    }
    return selectedFileIds
      .map((id) => documents.find((doc) => doc.id === id))
      .filter(Boolean);
  }, [selectedFileIds, documents]);

  const handleSelectionChange = (ids) => {
    const sanitizedIds = Array.isArray(ids) ? Array.from(new Set(ids)) : [];
    const limitedIds = hasAttachmentLimit
      ? sanitizedIds.slice(0, maxAttachments)
      : sanitizedIds;
    form.setValue("fileAttachment", limitedIds);
  };

  const handleRemoveFile = (id) => {
    const nextSelection = selectedFileIds.filter(
      (selectedId) => selectedId !== id
    );
    form.setValue("fileAttachment", nextSelection);
  };

  const formatDocumentType = (docType) => {
    if (!docType) return "-";
    return docType
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <>
      {!isProstateReferral && (
        <div>
          <FormField name="clinicalExamination">
            <label className={style.addReferralFormLabel}>
              Clinical Examination
            </label>
            <textarea
              className="form-control textareaform-control"
              style={{ minHeight: "126px" }}
              {...form.register("clinicalExamination")}
            />
          </FormField>
        </div>
      )}
      <div
        className={style.itemWrapper}
        style={{
          marginBottom: "0px",
          marginTop: !isProstateReferral ? "16px" : "-12px",
        }}
      >
        {!isLungReferral && (
          <>
            <div>
              <InvestigationSection
                form={form}
                name="laboratoryInvestigation"
                values={form.watch("laboratoryInvestigation")}
                laboratoryResults={laboratoryResults}
                radiologyResults={radiologyResults}
                withUnit={true}
              />
            </div>
            <div style={{ marginTop: "24px" }}>
              <InvestigationSection
                form={form}
                name="radiologyInvestigation"
                values={form.watch("radiologyInvestigation")}
                laboratoryResults={laboratoryResults}
                radiologyResults={radiologyResults}
                withHospital={true}
              />
            </div>
          </>
        )}
      </div>
      {maxAttachments > 0 && (
        <div className={style.itemWrapper} style={{ marginTop: "8px" }}>
          <div className={style.inputGroupGrid1Columns}>
            <div>
              <label className={style.addReferralFormLabel}>
                File Attachment
              </label>
              <p className={style.attachmentHelperText}>
                {[
                  hasAttachmentLimit
                    ? `Selected ${selectedFiles.length}/${maxAttachments} attachments`
                    : `Selected ${selectedFiles.length} attachment${
                        selectedFiles.length === 1 ? "" : "s"
                      }`,
                  allowedExtensions.length
                    ? `Allowed types: ${allowedExtensions
                        .map((ext) => ext.toUpperCase())
                        .join(", ")}`
                    : null,
                  maxFileSizeMb != null
                    ? `Max file size: ${maxFileSizeMb} MB`
                    : null,
                  maxFileSizeImageMb != null
                    ? `Max image size: ${maxFileSizeImageMb} MB`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" • ")}
              </p>
              {selectedFiles.length > 0 ? (
                <div className={style.selectedFilesList}>
                  {selectedFiles.map((file) => (
                    <div key={file.id} className={style.selectedFileCard}>
                      <div className={style.selectedFileHeader}>
                        <div className={style.selectedFileInfo}>
                          <div className={style.selectedFileRow}>
                            <span className={style.selectedFileLabel}>
                              File Name:
                            </span>
                            <span
                              className={style.selectedFileValue}
                              title={file.fileName}
                            >
                              {file.fileName || "-"}
                            </span>
                          </div>
                          <div className={style.selectedFileRow}>
                            <span className={style.selectedFileLabel}>
                              Document Type:
                            </span>
                            <span className={style.selectedFileValue}>
                              {formatDocumentType(file.documentType)}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className={style.removeFileButton}
                          onClick={() => handleRemoveFile(file.id)}
                          title="Remove selected file"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={style.selectedFilePlaceholder}>
                  No attachments selected yet.
                </p>
              )}
              <Button
                className={style.importResultsButton}
                style={{ marginTop: "12px" }}
                onClick={() => setIsFileAttachmentsModalOpen(true)}
              >
                {selectedFiles.length ? "Manage Attachments" : "Choose Files"}
              </Button>
              <Dialog
                title="Choose File Attachment"
                open={isFileAttachmentsModalOpen}
                onClose={() => setIsFileAttachmentsModalOpen(false)}
                width={1400}
              >
                <ChooseFileAttachment
                  items={documents}
                  selectedIds={selectedFileIds}
                  onSelectionChange={handleSelectionChange}
                  maxSelection={maxAttachments}
                  allowedExtensions={allowedExtensions}
                  onDone={() => setIsFileAttachmentsModalOpen(false)}
                  maxFileSizeMb={maxFileSizeMb}
                  maxFileSizeImageMb={maxFileSizeImageMb}
                />
              </Dialog>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
